import { c as _c } from "react/compiler-runtime";
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useAtom } from 'jotai';
import CardSlider from 'components/consumer/CardSlider';
import { NUMBER_OF_PRODUCTS_TO_SHOW } from 'commons/constants';
import ProductCardStatic from 'components/consumer/ProductCardStatic';
import ProductCardGridUI from 'components/consumer/ProductCardGridUI';
import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import CardCarousel from 'components/consumer/image-slider-standard/components/CardCarousel';
import useProductListingPageContext from 'data-hooks/useProductListingPageContext';
import useMobileColumnCount from 'components/consumer/ProductListingPage/state/mobileColumnCount/useMobileColumnCount';
import { createSearchProductClickHandler } from '@/components/consumer/consumer-search/algolia-insights-helpers/createSearchProductClickHandler';
import lastInsightsClickAtom from '@/components/consumer/consumer-search/advanced-search/atoms/lastInsightsClickAtom';
import CustomProductListItemPlaceholder from './CustomProductListItemPlaceholder';
const CustomProductListUI = t0 => {
  const $ = _c(24);
  const {
    listStyle: t1
  } = t0;
  const listStyle = t1 === undefined ? "Slider" : t1;
  const {
    filterStateKey,
    isAllDataLoading,
    loading,
    products,
    productsToPreload
  } = useProductListingPageContext();
  const mobileColumnCount = useMobileColumnCount();
  const [, setLastInsightsClick] = useAtom(lastInsightsClickAtom);
  const skeletonLoaderItems = products?.length > 0 && products?.length < NUMBER_OF_PRODUCTS_TO_SHOW ? NUMBER_OF_PRODUCTS_TO_SHOW - products.length : NUMBER_OF_PRODUCTS_TO_SHOW;
  let t2;
  if ($[0] !== filterStateKey || $[1] !== listStyle || $[2] !== loading || $[3] !== products || $[4] !== productsToPreload || $[5] !== setLastInsightsClick) {
    const allProductsToShow = [...products, ...(productsToPreload ?? []).map(_temp)];
    t2 = !loading && allProductsToShow?.map((t3, index) => {
      const {
        algoliaMetaData,
        campaign,
        id,
        isClearance,
        isFavorited,
        isHidden,
        label,
        moreVariantsCount,
        name,
        onDisplayInShowroomSlug,
        path,
        position,
        productFamily,
        productLabel,
        productType,
        quickship,
        secondaryImage,
        secondaryImageCropData,
        slug,
        variants,
        variantsToShow,
        variantsTotal
      } = t3;
      return <ProductCardStatic campaign={campaign} disableImageLazyLoading={listStyle === "Grid" && index < 12} enableMobileCarousel={false} filterStateKey={filterStateKey} hidden={isHidden} id={id} isFavorited={isFavorited} isClearance={isClearance} key={`${slug}-${index}`} label={label} layout="minimal-with-attributes" moreVariantsCount={moreVariantsCount} name={name} onClick={algoliaMetaData ? createSearchProductClickHandler(algoliaMetaData, setLastInsightsClick, {
        productId: id
      }) : undefined} onDisplayInShowroomSlug={onDisplayInShowroomSlug} path={path} position={position} productFamily={productFamily} productLabel={productLabel} productType={productType} quickship={quickship} secondaryImage={secondaryImage} secondaryImageCropData={secondaryImageCropData} slug={slug} variants={variants} variantsToShow={variantsToShow} variantsTotal={variantsTotal} />;
    });
    $[0] = filterStateKey;
    $[1] = listStyle;
    $[2] = loading;
    $[3] = products;
    $[4] = productsToPreload;
    $[5] = setLastInsightsClick;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const productsList = t2;
  if (listStyle === "Carousel") {
    let t3;
    if ($[7] !== productsList) {
      t3 = <FixedWidthContainer className="CustomProductListUI"><CardCarousel imageHeight={350} imageWidth={350} navType="Button" visibleSlides={4}>{productsList}</CardCarousel></FixedWidthContainer>;
      $[7] = productsList;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    return t3;
  }
  if (listStyle === "Slider") {
    let t3;
    if ($[9] !== productsList) {
      t3 = <div className="max-w-[1500px] mx-auto my-0"><CardSlider wide={false}>{productsList}</CardSlider></div>;
      $[9] = productsList;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    return t3;
  }
  if (!loading && !products?.length) {
    let t3;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <FixedWidthContainer className="max-[1023px]:!p-0"><div className="text-lg font-bold flex flex-col items-center justify-center px-0 py-8 min-h-[30vh]">Looks like these deals sold out in a flash! Check back soon for more opportunities to save big.</div></FixedWidthContainer>;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    return t3;
  }
  let t3;
  if ($[12] !== loading || $[13] !== skeletonLoaderItems) {
    t3 = loading && [...Array(skeletonLoaderItems)].map(_temp2);
    $[12] = loading;
    $[13] = skeletonLoaderItems;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== isAllDataLoading || $[16] !== loading || $[17] !== skeletonLoaderItems) {
    t4 = !loading && isAllDataLoading && [...Array(skeletonLoaderItems)].map(_temp3);
    $[15] = isAllDataLoading;
    $[16] = loading;
    $[17] = skeletonLoaderItems;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== mobileColumnCount || $[20] !== productsList || $[21] !== t3 || $[22] !== t4) {
    t5 = <FixedWidthContainer className="max-[1023px]:!p-0"><ProductCardGridUI columnsOnDesktop="auto" columnsOnMobile={mobileColumnCount} limitWidth={false}>{t3}{productsList}{t4}</ProductCardGridUI></FixedWidthContainer>;
    $[19] = mobileColumnCount;
    $[20] = productsList;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  return t5;
};
CustomProductListUI.propTypes = {
  listStyle: PropTypes.oneOf(['Slider', 'Carousel', 'Grid'])
};
export default CustomProductListUI;
function _temp(product) {
  return {
    ...product,
    isHidden: true
  };
}
function _temp2(item, index_0) {
  return <CustomProductListItemPlaceholder key={`placeholder-${index_0}`} />;
}
function _temp3(item_0, index_1) {
  return <CustomProductListItemPlaceholder key={`placeholder-${index_1}`} />;
}